import { useEffect, useMemo, useState } from 'react';
import type { CSSProperties, MouseEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import type { PublicMenu, PublicMenuItem } from '@order-pilot/shared';
import { API_URL } from '@/lib/api';

type Lang = 'en' | 'ar';

/** Only the page's own words are translated; item names are as the shop typed them. */
const WORDS: Record<Lang, Record<string, string>> = {
  en: {
    search: 'Search the menu',
    all: 'All',
    soldOut: 'Sold out',
    combo: 'Combo',
    table: 'Table',
    nothing: 'Nothing matches your search.',
    unavailableTitle: 'Menu not available',
    unavailable: 'This menu is not available right now. Please ask a member of staff.',
    offline: 'Could not load the menu. Check your connection and try again.',
    retry: 'Try again',
    updated: 'Menu updated',
    theme: 'Colours',
    house: 'House',
  },
  ar: {
    search: 'ابحث في القائمة',
    all: 'الكل',
    soldOut: 'نفدت الكمية',
    combo: 'وجبة',
    table: 'طاولة',
    nothing: 'لا توجد نتائج مطابقة.',
    unavailableTitle: 'القائمة غير متاحة',
    unavailable: 'هذه القائمة غير متاحة حاليًا. يرجى سؤال أحد الموظفين.',
    offline: 'تعذّر تحميل القائمة. تحقق من الاتصال وحاول مرة أخرى.',
    retry: 'إعادة المحاولة',
    updated: 'آخر تحديث',
    theme: 'الألوان',
    house: 'الأصلي',
  },
};

// ── Themes (from the "3D Cafe Menu" design) ─────────────────────────────────

interface Theme {
  stage: string;
  bg: string;
  hero: string;
  card: string;
  plate: string;
  text: string;
  accent: string;
  onAccent: string;
  dark: boolean;
}

const PRESETS: Record<string, Theme> = {
  Espresso: { stage: 'radial-gradient(circle at 30% 0,#3b2a20,#120c09)', bg: '#f3ebe1', hero: '#e7d8c5', card: '#fbf6ef', plate: '#e9dccb', text: '#2b1d14', accent: '#8a4b2a', onAccent: '#fff', dark: false },
  Midnight: { stage: 'radial-gradient(circle at 30% 0,#1d2340,#05060c)', bg: '#10131f', hero: '#181d33', card: '#1b2036', plate: '#232a45', text: '#eef0ff', accent: '#f2b84b', onAccent: '#1b1404', dark: true },
  Matcha: { stage: 'radial-gradient(circle at 30% 0,#2c3a28,#0b100a)', bg: '#eef1e6', hero: '#d9e3c8', card: '#f8faf2', plate: '#dfe7d0', text: '#1f2a1b', accent: '#4f7a3a', onAccent: '#fff', dark: false },
  Cobalt: { stage: 'radial-gradient(circle at 30% 0,#1c2a6b,#060a1f)', bg: '#f8fafc', hero: '#dfe4fb', card: '#ffffff', plate: '#eef1fb', text: '#0f172a', accent: '#1C36B5', onAccent: '#fff', dark: false },
};

/** Mix [hex] toward white by [amount] (0..1). */
function tint(hex: string, amount: number): string {
  const n = parseInt(hex.slice(1), 16);
  const ch = (shift: number) => {
    const c = (n >> shift) & 255;
    return Math.round(c + (255 - c) * amount).toString(16).padStart(2, '0');
  };
  return `#${ch(16)}${ch(8)}${ch(0)}`;
}

/** The shop's own colour, as the default theme. */
function houseTheme(accent: string): Theme {
  const a = /^#[0-9a-fA-F]{6}$/.test(accent) ? accent : '#2D5E3A';
  return {
    stage: `radial-gradient(circle at 30% 0,${a},#0b0b0f)`,
    bg: '#f8fafc',
    hero: tint(a, 0.85),
    card: '#ffffff',
    plate: tint(a, 0.92),
    text: '#0f172a',
    accent: a,
    onAccent: '#fff',
    dark: false,
  };
}

const cardShadow = (t: Theme) =>
  t.dark
    ? '0 18px 30px -12px rgba(0,0,0,.7),inset 0 1px 0 rgba(255,255,255,.06)'
    : '0 18px 30px -14px rgba(60,40,20,.35),0 2px 0 rgba(255,255,255,.9) inset';
const BTN_SHADOW = '0 6px 0 -1px rgba(0,0,0,.25),0 10px 16px -6px rgba(0,0,0,.35)';
const SERIF = "'Instrument Serif', Georgia, serif";
const SANS = "'Bricolage Grotesque', system-ui, sans-serif";

/** The design's two fonts, loaded once, only on this page. */
function useMenuFonts() {
  useEffect(() => {
    const id = 'op-menu-fonts';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href =
      'https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@400;600;800&family=Instrument+Serif:ital@0;1&display=swap';
    document.head.appendChild(link);
  }, []);
}

// ── Small persisted preferences ─────────────────────────────────────────────

function readPref(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function writePref(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode — not essential */
  }
}
function initialLang(): Lang {
  const saved = readPref('op.menu.lang');
  if (saved === 'ar' || saved === 'en') return saved;
  return typeof navigator !== 'undefined' && navigator.language?.startsWith('ar') ? 'ar' : 'en';
}

// ── Data ────────────────────────────────────────────────────────────────────

class MenuLoadError extends Error {
  constructor(readonly notFound: boolean) {
    super(notFound ? 'not found' : 'failed');
  }
}

async function loadMenu(code: string, signal?: AbortSignal): Promise<PublicMenu> {
  const response = await fetch(`${API_URL}/menu/${encodeURIComponent(code)}`, { signal });
  if (response.status === 404) throw new MenuLoadError(true);
  if (!response.ok) throw new MenuLoadError(false);
  return (await response.json()) as PublicMenu;
}

const reducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// ── Pieces ──────────────────────────────────────────────────────────────────

/** "Sign leban" → "Sign" + italic accent "leban", as in the design. */
function Title({ text, accent }: { text: string; accent: string }) {
  const cut = text.trim().lastIndexOf(' ');
  const style: CSSProperties = { margin: '22px 0 4px', font: `400 54px/0.95 ${SERIF}`, letterSpacing: '-.01em', overflowWrap: 'anywhere' };
  if (cut <= 0) return <h1 style={style}>{text}</h1>;
  return (
    <h1 style={style}>
      {text.slice(0, cut)} <i style={{ color: accent }}>{text.slice(cut + 1)}</i>
    </h1>
  );
}

function ItemCard({
  item,
  currency,
  t,
  words,
}: {
  item: PublicMenuItem;
  currency: string;
  t: Theme;
  words: Record<string, string>;
}) {
  const [tilt, setTilt] = useState<[number, number]>([0, 0]);
  const soldOut = !item.available;
  const money = (v: string) => `${currency} ${v}`;

  const move = (e: MouseEvent<HTMLLIElement>) => {
    if (reducedMotion()) return;
    const r = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    setTilt([x * 14, -y * 14]);
  };
  const tilted = tilt[0] !== 0 || tilt[1] !== 0;

  return (
    <li
      onMouseMove={move}
      onMouseLeave={() => setTilt([0, 0])}
      style={{
        display: 'flex',
        gap: 14,
        alignItems: 'center',
        padding: 12,
        borderRadius: 24,
        background: t.card,
        boxShadow: cardShadow(t),
        transform: `rotateX(${tilt[1]}deg) rotateY(${tilt[0]}deg) translateZ(${tilted ? 12 : 0}px)`,
        transition: 'transform .2s ease-out, background .5s',
        opacity: soldOut ? 0.5 : 1,
        transformStyle: 'preserve-3d',
      }}
    >
      <div
        style={{
          flex: 'none',
          width: 92,
          height: 92,
          borderRadius: 18,
          background: t.plate,
          overflow: 'hidden',
          transform: 'translateZ(30px)',
          boxShadow: '0 14px 22px -8px rgba(0,0,0,.45)',
        }}
      >
        {item.imageUrl ? (
          <img
            src={`${API_URL}${item.imageUrl}`}
            alt=""
            loading="lazy"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center', font: `400 34px ${SERIF}`, color: t.accent }}>
            {item.name.trim().charAt(0).toUpperCase()}
          </div>
        )}
      </div>

      <div style={{ flex: 1, minWidth: 0, transform: 'translateZ(15px)' }}>
        <div style={{ fontWeight: 600, fontSize: 16 }}>{item.name}</div>
        {item.description ? (
          <div style={{ fontSize: 13, opacity: 0.65, marginTop: 2 }}>{item.description}</div>
        ) : null}

        {item.variants.length > 0 ? (
          <ul style={{ display: 'flex', flexWrap: 'wrap', gap: 6, margin: '10px 0 0', padding: 0, listStyle: 'none' }}>
            {item.variants.map((v) => (
              <li
                key={v.label}
                style={{ borderRadius: 10, padding: '5px 10px', fontSize: 12, background: t.plate, color: t.text }}
              >
                {v.label}
                {v.price !== null ? (
                  <b style={{ marginInlineStart: 5, color: t.accent, fontVariantNumeric: 'tabular-nums' }}>{money(v.price)}</b>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, gap: 8 }}>
          {item.price !== null && item.variants.length === 0 ? (
            <span style={{ fontWeight: 800, color: t.accent, fontVariantNumeric: 'tabular-nums' }}>{money(item.price)}</span>
          ) : (
            <span />
          )}
          <span style={{ display: 'flex', gap: 6 }}>
            {item.combo ? (
              <span style={{ borderRadius: 12, padding: '6px 10px', fontSize: 12, fontWeight: 700, background: t.accent, color: t.onAccent, boxShadow: BTN_SHADOW }}>
                {words.combo}
              </span>
            ) : null}
            {soldOut ? (
              <span style={{ borderRadius: 12, padding: '6px 10px', fontSize: 12, fontWeight: 700, background: t.plate, color: t.text }}>
                {words.soldOut}
              </span>
            ) : null}
          </span>
        </div>
      </div>
    </li>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

/**
 * The customer's menu, opened from a table QR code (the "3D Cafe Menu"
 * design). Public: no login, no cookies, nothing written anywhere — it only
 * shows what the till sent. View-only: there is no ordering from here.
 */
export function PublicMenuPage() {
  useMenuFonts();
  const { code = '' } = useParams();
  const [params] = useSearchParams();
  const table = params.get('t')?.slice(0, 40) ?? '';
  const [lang, setLang] = useState<Lang>(initialLang);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string | null>(null);
  const [themeName, setThemeName] = useState<string>(() => readPref(`op.menu.theme.${code}`) ?? 'House');
  const [showThemes, setShowThemes] = useState(false);
  const words = WORDS[lang];

  useEffect(() => writePref('op.menu.lang', lang), [lang]);
  useEffect(() => writePref(`op.menu.theme.${code}`, themeName), [code, themeName]);

  const query = useQuery({
    queryKey: ['public-menu', code],
    queryFn: ({ signal }) => loadMenu(code, signal),
    retry: (count, error) => !(error instanceof MenuLoadError && error.notFound) && count < 2,
    // A till marks things sold out during service; keep the page fresh.
    refetchInterval: 120_000,
  });
  const menu = query.data;

  const house = useMemo(() => houseTheme(menu?.branding.accent ?? '#2D5E3A'), [menu?.branding.accent]);
  const themes = useMemo(() => ({ House: house, ...PRESETS }), [house]);
  const t = themes[themeName as keyof typeof themes] ?? house;

  useEffect(() => {
    if (menu) document.title = menu.branding.title || menu.shopName;
  }, [menu]);

  const categories = useMemo(() => {
    if (!menu) return [];
    const q = search.trim().toLowerCase();
    return menu.categories
      .filter((c) => category === null || c.name === category)
      .map((c) => ({
        name: c.name,
        items: q
          ? c.items.filter((i) => i.name.toLowerCase().includes(q) || i.description.toLowerCase().includes(q))
          : c.items,
      }))
      .filter((c) => c.items.length > 0);
  }, [menu, search, category]);

  // A centered, phone-width reading column on a wide screen — not a decorative
  // phone mockup. A customer only ever opens this on their own phone, where it
  // already fills the screen; the column just keeps it readable on a desktop
  // preview instead of stretching text edge to edge.
  const shell = (children: React.ReactNode) => (
    <div
      lang={lang}
      className="flex min-h-screen justify-center sm:items-start"
      style={{ background: t.stage, fontFamily: SANS, transition: 'background .5s' }}
    >
      <div
        className="w-full sm:w-[390px]"
        style={{ background: t.bg, color: t.text, transition: 'background .5s,color .5s', position: 'relative', minHeight: '100vh' }}
      >
        {children}
      </div>
    </div>
  );

  const iconButton: CSSProperties = {
    border: 0,
    cursor: 'pointer',
    height: 44,
    minWidth: 44,
    padding: '0 12px',
    borderRadius: 14,
    background: t.card,
    color: t.text,
    font: `600 14px ${SANS}`,
    boxShadow: BTN_SHADOW,
  };

  if (query.isError || (!menu && !query.isPending)) {
    const notFound = query.error instanceof MenuLoadError && query.error.notFound;
    return shell(
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '80vh', padding: 24, textAlign: 'center' }}>
        <div>
          <h1 style={{ margin: 0, font: `400 40px/1 ${SERIF}` }}>{words.unavailableTitle}</h1>
          <p style={{ marginTop: 12, fontSize: 14, opacity: 0.7 }}>{notFound ? words.unavailable : words.offline}</p>
          {!notFound ? (
            <button type="button" onClick={() => void query.refetch()} style={{ ...iconButton, marginTop: 16, background: t.accent, color: t.onAccent }}>
              {words.retry}
            </button>
          ) : null}
        </div>
      </div>,
    );
  }

  const chips = menu && menu.categories.length > 1 ? [null, ...menu.categories.map((c) => c.name)] : [];

  return shell(
    <>
      <header style={{ padding: '48px 22px 26px', background: t.hero, transition: 'background .5s' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            {menu?.logoUrl ? (
              <img src={`${API_URL}${menu.logoUrl}`} alt="" style={{ width: 36, height: 36, borderRadius: 10, objectFit: 'cover', background: '#fff' }} />
            ) : null}
            <div style={{ fontSize: 12, letterSpacing: '.14em', textTransform: 'uppercase', opacity: 0.7, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {menu?.branchName}
              {table ? ` · ${words.table} ${table}` : ''}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flex: 'none' }}>
            <button type="button" onClick={() => setLang(lang === 'en' ? 'ar' : 'en')} style={iconButton}>
              {lang === 'en' ? 'ع' : 'EN'}
            </button>
            <button
              type="button"
              aria-label={words.theme}
              aria-expanded={showThemes}
              onClick={() => setShowThemes((v) => !v)}
              style={{ ...iconButton, font: '600 18px sans-serif' }}
            >
              ◐
            </button>
          </div>
        </div>
        {menu ? <Title text={menu.branding.title || menu.shopName} accent={t.accent} /> : <div style={{ height: 80 }} />}
        {menu?.branding.welcome ? <p style={{ margin: 0, fontSize: 14, opacity: 0.75 }}>{menu.branding.welcome}</p> : null}
      </header>

      {showThemes ? (
        <div style={{ padding: '16px 22px', display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 8, background: t.card }}>
          {Object.entries(themes).map(([name, th]) => (
            <button
              key={name}
              type="button"
              onClick={() => setThemeName(name)}
              style={{
                cursor: 'pointer',
                border: name === themeName ? `2px solid ${t.accent}` : '2px solid transparent',
                borderRadius: 16,
                padding: '8px 2px',
                background: 'transparent',
                color: t.text,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 6,
                font: `600 11px ${SANS}`,
              }}
            >
              <span style={{ width: 36, height: 36, borderRadius: '50%', background: th.accent, boxShadow: 'inset 0 -6px 10px rgba(0,0,0,.25),0 4px 10px rgba(0,0,0,.2)' }} />
              {name === 'House' ? words.house : name}
            </button>
          ))}
        </div>
      ) : null}

      <div style={{ padding: '16px 22px 0' }}>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={words.search}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            border: 0,
            borderRadius: 16,
            padding: '12px 16px',
            font: `400 14px ${SANS}`,
            background: t.card,
            color: t.text,
            boxShadow: cardShadow(t),
            outline: 'none',
          }}
        />
      </div>

      {chips.length > 0 ? (
        <nav style={{ display: 'flex', gap: 8, overflowX: 'auto', padding: '16px 22px 8px' }}>
          {chips.map((name) => {
            const on = category === name;
            return (
              <button
                key={name ?? '__all'}
                type="button"
                onClick={() => setCategory(name)}
                style={{
                  flex: 'none',
                  cursor: 'pointer',
                  border: 0,
                  borderRadius: 999,
                  padding: '10px 16px',
                  font: `600 14px ${SANS}`,
                  background: on ? t.accent : t.card,
                  color: on ? t.onAccent : t.text,
                  boxShadow: on ? BTN_SHADOW : '0 4px 10px -4px rgba(0,0,0,.25)',
                  transform: on ? 'translateY(-2px)' : 'none',
                  transition: 'all .25s',
                }}
              >
                {name ?? words.all}
              </button>
            );
          })}
        </nav>
      ) : null}

      <main style={{ padding: '8px 22px 40px', perspective: 900 }}>
        {query.isPending ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 22 }}>
            {[0, 1, 2].map((i) => (
              <div key={i} className="animate-pulse" style={{ height: 116, borderRadius: 24, background: t.card, boxShadow: cardShadow(t) }} />
            ))}
          </div>
        ) : categories.length === 0 ? (
          <p style={{ marginTop: 40, textAlign: 'center', fontSize: 14, opacity: 0.65 }}>{words.nothing}</p>
        ) : (
          categories.map((c) => (
            <section key={c.name}>
              <h2 style={{ margin: '22px 0 14px', font: `400 28px ${SERIF}` }}>{c.name}</h2>
              <ul style={{ display: 'flex', flexDirection: 'column', gap: 16, margin: 0, padding: 0, listStyle: 'none' }}>
                {c.items.map((item) => (
                  <ItemCard key={item.name} item={item} currency={menu!.currency} t={t} words={words} />
                ))}
              </ul>
            </section>
          ))
        )}
        {menu ? (
          <p style={{ marginTop: 32, textAlign: 'center', fontSize: 11, opacity: 0.45 }}>
            {words.updated} {new Date(menu.updatedAt).toLocaleString(lang === 'ar' ? 'ar' : undefined)}
          </p>
        ) : null}
      </main>
    </>,
  );
}
